import { useEffect, useState } from 'react';
import { App, Button, Form, Input, Modal, Select, Space, Typography } from 'antd';
import { RichTextEditor } from './RichTextEditor';
import { useAgents, useSendEmail } from '../features/email/api';
import { errorMessage } from '../api/client';

interface Props {
  open: boolean;
  onClose: () => void;
  contactId: string;
  contactEmail: string | null;
  contactName: string;
  onSent?: (threadId: string) => void;
}

/** Compose a one-to-one email to a contact as one of the virtual agents. */
export function EmailComposer({ open, onClose, contactId, contactEmail, contactName, onSent }: Props) {
  const { message } = App.useApp();
  const agents = useAgents();
  const send = useSendEmail();
  const [agentId, setAgentId] = useState<string>();
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const active = (agents.data ?? []).filter((a) => a.isActive);

  useEffect(() => {
    if (open && !agentId && active.length) setAgentId(active[0].id);
  }, [open, agentId, active]);

  const reset = () => {
    setSubject('');
    setHtml('');
    setAgentId(undefined);
  };
  const cancel = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!agentId) return message.warning('Choose an agent to send as');
    if (!subject.trim() || !html.trim()) return message.warning('Add a subject and a message');
    try {
      const r = await send.mutateAsync({ agentId, contactId, subject: subject.trim(), html });
      message.success(`Email sent to ${contactEmail}`);
      reset();
      onSent?.(r.threadId);
      onClose();
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <Modal
      title={`Email ${contactName}`}
      open={open}
      onCancel={cancel}
      width={720}
      destroyOnHidden
      footer={
        <Space>
          <Button onClick={cancel}>Cancel</Button>
          <Button type="primary" onClick={() => void submit()} loading={send.isPending} disabled={!contactEmail}>Send</Button>
        </Space>
      }
    >
      {!contactEmail && <Typography.Paragraph type="warning">This contact has no email address.</Typography.Paragraph>}
      <Form layout="vertical">
        <Form.Item label="From">
          <Select
            value={agentId}
            onChange={setAgentId}
            loading={agents.isLoading}
            placeholder={agents.isLoading ? 'Loading agents…' : active.length ? 'Choose an agent' : 'No active agents. Create one under Admin → Agents.'}
            options={active.map((a) => ({ value: a.id, label: `${a.name} <${a.email}>${a.position ? ` · ${a.position}` : ''}` }))}
          />
        </Form.Item>
        <Form.Item label="To">
          <Input value={contactEmail ?? ''} readOnly />
        </Form.Item>
        <Form.Item label="Subject">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (merge tags like {{first_name}} work)" maxLength={500} />
        </Form.Item>
        <Form.Item label="Message">
          <RichTextEditor value={html} onChange={setHtml} placeholder="Hi {{first_name|there}}, …" minHeight={220} />
        </Form.Item>
      </Form>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>The agent's signature is appended automatically.</Typography.Text>
    </Modal>
  );
}
